import { createModule } from "saga-slice";
import { call, put } from "redux-saga/effects";
import { createReceiptNote, generateDocument, generatePdf, getNewBRNumber, getReceiptNotesByPurchaseId, search, updateReceiptNote, validateReceiptNote } from "../services/receiptNoteService";

const receiptNoteModule = createModule({
  name: "receiptNote",
  initialState: {
    isLoading: false,
    error: null,
    receiptNote: null,
    receiptNotes: [],
    newBRNumber: null,
    dateFrom: null,
    dateTo: null,
    totalElements: 0, // Total number of records
    totalPages: 0, // Total number of pages
    pageNumber: 0, // Current page number
    pageSize: 10, // Size of each page
  },
  
  reducers: {
    search: (state) => {
      state.isLoading = true;
    },
    fetchNewBRNumber: (state) => {
      state.isLoading = true;
    },
    createReceiptNote: (state, action) => {
      state.isLoading = true;
      state.error = null;
    },
    generatePdfRequest: (state) => {
      state.isLoading = true;
    },
    
    updateReceiptNote: (state, action) => {
      state.isLoading = false;
    },
   
    finishFetching: (state) => {
      state.isLoading = false;
    },
    fetchedData: (state, payload) => {
      state.isLoading = false;
      state.receiptNotes = payload.body.content;
      state.totalElements = payload.body.totalElements; // Assuming totalElements is in payload
      state.totalPages = payload.body.totalPages; // Assuming totalPages is in payload
      state.pageNumber = payload.body.pageNumber;
      state.dateFrom = payload.body.dateFrom;
      state.dateTo = payload.body.dateTo;
    },
    getReceiptNotesByPurchaseId: (state) => {
      state.isLoading = true;
    },

    // Line Item Reducers
    updateLineItem: (state, action) => {
      const { receiptNoteId, index, name, value } = action.payload;

      // Find the receipt note
      const receiptNote = state.receiptNotes.find((rn) => rn.id === receiptNoteId);

      if (receiptNote) {
        // Update the line item
        receiptNote.lines[index][name] = value;
      }

      // If a single receipt note is loaded, update it as well
      if (state.receiptNote && state.receiptNote.id === receiptNoteId) {
        state.receiptNote.lines[index][name] = value;
      }
    },
    // Receipt Note specific reducers
    setReceiptNote: (state, action) => {
      state.receiptNote = action.payload;
      state.isLoading = false;
    },

    addLineItem: (state, action) => {
      const { receiptNoteId, lineItem } = action.payload;
      const receiptNote = state.receiptNotes.find((rn) => rn.id === receiptNoteId);

      if (receiptNote) {
        receiptNote.lines.push(lineItem);
      }

      if (state.receiptNote && state.receiptNote.id === receiptNoteId) {
        state.receiptNote.lines.push(lineItem);
      }
    },
    deleteLineItem: (state, action) => {
      const { receiptNoteId, index } = action.payload;

      const receiptNote = state.receiptNotes.find((rn) => rn.id === receiptNoteId);
      if (receiptNote) {
        receiptNote.lines.splice(index, 1);
      }

      if (state.receiptNote && state.receiptNote.id === receiptNoteId) {
        state.receiptNote.lines.splice(index, 1);
      }
    },
    
    createReceiptNoteSuccess: (state) => {
      state.isLoading = false;
    },
    createReceiptNoteFailure: (state, action) => {
      state.isLoading = false;
      state.error = action.payload;
    },
    generatePdfSuccess: (state) => {
      state.isLoading = false;
    },
    generatePdfFailure: (state, action) => {
      state.isLoading = false;
      state.error = action.payload;
    },
    validateReceiptNote: (state) => {
      state.isLoading = true;
    },
    search: (state) => {
      state.isLoading = true;
    },
    generateDocument: (state) => {
      state.isLoading = true;
    },
    setNewBRNumber: (state, action) => {
      state.newBRNumber = action;
      state.isLoading = false;
    },
    setReceiptNotes: (state, action) => {
      state.receiptNotes = action;
      state.isLoading = false;
    },
    setError: (state, action) => {
      state.isLoading = false;
      state.error = action.payload;
    },
  },
  sagas: (A) => ({
    *[A.search]({ payload }) {
      try {
        const { data } = yield call(search, payload);
        yield put(A.finishFetching());
        yield put(A.fetchedData(data));
      } catch (e) {
        console.log(e);
        yield put(A.finishFetching());
        yield put(A.fetchError());
      }
    },
    *[A.generateDocument]({ payload }) {
          try {
            const pdfData = yield call(generateDocument, payload.id);
            const blob = new Blob([pdfData], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            window.open(url, '_blank'); // Open the PDF in a new window
            yield put(A.generatePdfSuccess());
          } catch (error) {
            yield put(A.setError(error.message));
          }
        },
    *[A.validateReceiptNote]({ payload }) {
      try {
        yield call(validateReceiptNote, payload.id);
        yield put(A.createReceiptNoteSuccess());
        if (payload.onSuccess) {
          yield call(payload.onSuccess);
        }
      } catch (error) {
        yield put(A.setError(error.message));
      }
    },  
    *[A.fetchNewBRNumber]({ payload }) {
      try {
        const { data } = yield call(getNewBRNumber);
        yield put(A.setNewBRNumber(data.body));
      } catch (error) {
        yield put(A.setError(error.message));
      }
    },
    *[A.createReceiptNote]({ payload }) {
      try {
        yield call(createReceiptNote, payload.data);
        yield put(A.createReceiptNoteSuccess());

        if (payload.onSuccess) {
          yield call(payload.onSuccess);
        }
      } catch (error) {
        yield put(A.createReceiptNoteFailure(error.message));
        if (payload.onError) {
          yield call(payload.onError, error);
        }
      }
    },
    *[A.updateReceiptNote]({ payload }) {
      try {
        const { data } = yield call(updateReceiptNote, payload.id, payload.data);
        yield put(A.createReceiptNoteSuccess());
        if (payload.onSuccess) {
          yield call(payload.onSuccess, data);
        }
      } catch (error) {
        yield put(A.setError(error.message));
      }
    },
   
    *[A.getReceiptNotesByPurchaseId]({ payload }) {
      try {
        const { data } = yield call(getReceiptNotesByPurchaseId, payload.id);
        yield put(A.setReceiptNotes(data));
      } catch (error) {
        yield put(A.setError(error.message));
      }
    },
    *[A.generatePdfRequest]({ payload }) {
      try {
        const pdfData = yield call(generatePdf, payload);
        const blob = new Blob([pdfData], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        window.open(url, '_blank'); // Open the PDF in a new window
        yield put(A.generatePdfSuccess());
      } catch (error) {
        yield put(A.generatePdfFailure(error.message));
      }
    },
  }),

});

export default receiptNoteModule;
export const receiptNoteActions = receiptNoteModule.actions;